import { useEffect, useState } from "react";

export function usePageMotion() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const root = document.documentElement;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const compactMotion = window.matchMedia(
      "(max-width: 820px), (hover: none) and (pointer: coarse)"
    );
    const revealNodes = Array.from(
      document.querySelectorAll<HTMLElement>("[data-reveal]")
    );
    const scrollZones = Array.from(
      document.querySelectorAll<HTMLElement>("[data-scroll-zone]")
    );
    const hero = document.querySelector<HTMLElement>(".hero");
    let frame = 0;
    let viewportHeight = window.innerHeight;
    let heroTravel = Math.max(1, (hero?.offsetHeight ?? viewportHeight) - viewportHeight);
    let lastScrolled = false;

    if (!reducedMotion.matches) root.classList.add("motion-ready");

    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-revealed");
          revealObserver.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -9%", threshold: 0.08 }
    );

    const sectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.classList.toggle("is-inview", entry.isIntersecting);
        });
      },
      { rootMargin: "18% 0px 18%", threshold: 0 }
    );

    revealNodes.forEach((node) => revealObserver.observe(node));
    scrollZones.forEach((node) => sectionObserver.observe(node));

    const update = () => {
      frame = 0;
      const y = window.scrollY;
      const viewport = viewportHeight;
      const pageTravel = Math.max(
        1,
        document.documentElement.scrollHeight - viewport
      );
      const pageProgress = reducedMotion.matches
        ? 0.38
        : Math.min(1, Math.max(0, y / pageTravel));
      const zoneMeasurements = compactMotion.matches
        ? []
        : scrollZones.map((zone) => ({
            zone,
            rect: zone.getBoundingClientRect()
          }));

      root.classList.toggle("motion-ready", !reducedMotion.matches);
      const nextScrolled = y > 56;
      if (nextScrolled !== lastScrolled) {
        lastScrolled = nextScrolled;
        setIsScrolled(nextScrolled);
      }
      if (compactMotion.matches) {
        root.style.removeProperty("--page-scroll");
        root.style.removeProperty("--page-progress");
        root.style.removeProperty("--page-hue");
        root.style.removeProperty("--page-bg-shift");
      } else {
        root.style.setProperty("--page-scroll", y.toFixed(0));
        root.style.setProperty("--page-progress", pageProgress.toFixed(4));
        root.style.setProperty(
          "--page-hue",
          `${(-14 + pageProgress * 52).toFixed(2)}deg`
        );
        root.style.setProperty(
          "--page-bg-shift",
          `${(-2.5 + pageProgress * 5).toFixed(2)}%`
        );
      }

      if (hero) {
        const rawProgress = Math.min(1, Math.max(0, y / heroTravel));
        const stormProgress = reducedMotion.matches ? 1 : rawProgress;
        const stormEased = stormProgress * stormProgress * (3 - 2 * stormProgress);
        const copyProgress = reducedMotion.matches ? 1 : Math.min(1, rawProgress / 0.72);
        const copyEased = copyProgress * copyProgress * (3 - 2 * copyProgress);
        const fallProgress = Math.min(1, Math.max(0, (stormEased - 0.72) / 0.28));
        const copyTravel = window.innerWidth <= 560 ? 72 : window.innerWidth <= 820 ? 104 : 148;
        hero.style.setProperty("--hero-progress", stormEased.toFixed(4));
        hero.style.setProperty("--hero-fall", fallProgress.toFixed(4));
        hero.style.setProperty("--hero-copy-lift", `${(-copyTravel * copyEased).toFixed(2)}px`);
      }

      revealNodes.forEach((node) => {
        if (node.classList.contains("is-revealed")) return;
        const rect = node.getBoundingClientRect();
        if (rect.top <= viewport * 0.9) {
          node.classList.add("is-revealed");
          revealObserver.unobserve(node);
        }
      });

      if (zoneMeasurements.length) {
        zoneMeasurements.forEach(({ zone, rect }) => {
          const progress = Math.min(
            1,
            Math.max(0, (viewport - rect.top) / (viewport + rect.height))
          );
          zone.style.setProperty("--section-progress", progress.toFixed(4));
          zone.style.setProperty(
            "--section-shift",
            ((progress - 0.5) * 2).toFixed(4)
          );
        });
      }
    };

    const requestUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    const handleResize = () => {
      viewportHeight = window.innerHeight;
      heroTravel = Math.max(
        1,
        (hero?.offsetHeight ?? viewportHeight) - viewportHeight
      );
      requestUpdate();
    };

    update();
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", handleResize);
    reducedMotion.addEventListener("change", requestUpdate);
    compactMotion.addEventListener("change", requestUpdate);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      revealObserver.disconnect();
      sectionObserver.disconnect();
      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", handleResize);
      reducedMotion.removeEventListener("change", requestUpdate);
      compactMotion.removeEventListener("change", requestUpdate);
      root.classList.remove("motion-ready");
      root.style.removeProperty("--page-scroll");
      root.style.removeProperty("--page-progress");
      root.style.removeProperty("--page-hue");
      root.style.removeProperty("--page-bg-shift");
      [
        "--hero-progress",
        "--hero-fall",
        "--hero-copy-lift"
      ].forEach((property) => hero?.style.removeProperty(property));
    };
  }, []);

  return isScrolled;
}
